import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ComponentRef,
  type ReactNode
} from 'react'
import { Tooltip as TooltipPrimitive } from 'radix-ui'
import { TOOLTIP_MOTION_CLASS } from '#lib/motion'
import { cn } from '#lib/utils'
import { useLayerPortalTarget } from '#components/LayerHost'

export const TooltipProvider = TooltipPrimitive.Provider
export const Tooltip = TooltipPrimitive.Root
export const TooltipTrigger = TooltipPrimitive.Trigger

/**
 * 禁用按钮收不到指针事件。需要说明为什么不能用时，由这层触发区承接悬停与键盘聚焦：
 * 只看提示、不执行动作，也不向父级传递点击；内部按钮保持原生禁用。
 * active 为 false 时只是透明外壳：按钮在可用与禁用之间切换时外层不变，按钮不重建、焦点不丢。
 */
export const DisabledHintTrigger = forwardRef<
  HTMLSpanElement,
  ComponentPropsWithoutRef<'span'> & { label?: string; active?: boolean; children: ReactNode }
>(function DisabledHintTrigger(
  { label, active = true, tabIndex = 0, className, onClick, children, ...props },
  ref
) {
  return (
    <span
      ref={ref}
      {...props}
      {...(active
        ? { role: 'group', 'aria-disabled': true, 'aria-label': label, title: '', tabIndex }
        : {})}
      onClick={(event) => {
        if (active) {
          event.preventDefault()
          event.stopPropagation()
        }
        onClick?.(event)
      }}
      className={cn(
        'inline-flex shrink-0 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        className
      )}
    >
      {children}
    </span>
  )
})

export interface TooltipContentProps
  extends ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> {
  /** Preferred width cap; numbers are pixels and strings are CSS lengths. */
  maxWidth?: number | string
}

export const TooltipContent = forwardRef<
  ComponentRef<typeof TooltipPrimitive.Content>,
  TooltipContentProps
>(function TooltipContent(
  {
    className,
    side = 'bottom',
    sideOffset = 8,
    collisionPadding = 8,
    collisionBoundary,
    maxWidth = '20rem',
    style,
    onWheel,
    onTouchMove,
    ...props
  },
  ref
) {
  const tooltipTarget = useLayerPortalTarget('tooltip')
  // The modal scroll guard sees portalled tips outside its content surface.
  return (
    <TooltipPrimitive.Portal container={tooltipTarget.container ?? undefined}>
      <TooltipPrimitive.Content
        ref={ref}
        side={side}
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
        collisionBoundary={collisionBoundary ?? tooltipTarget.container ?? undefined}
        className={cn(
          'pointer-events-auto origin-[var(--radix-tooltip-content-transform-origin)] overflow-y-auto overscroll-contain whitespace-normal [overflow-wrap:anywhere] rounded-md border border-border bg-popover px-2 py-1 text-[11px] leading-4 text-popover-foreground shadow-lg',
          TOOLTIP_MOTION_CLASS,
          className
        )}
        style={{
          ...style,
          minWidth: 0,
          minHeight: 0,
          maxWidth: `min(${typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth}, var(--radix-tooltip-content-available-width))`,
          maxHeight: 'var(--radix-tooltip-content-available-height)',
          zIndex: tooltipTarget.zIndex
        }}
        {...props}
        onWheel={(event) => {
          onWheel?.(event)
          event.stopPropagation()
        }}
        onTouchMove={(event) => {
          onTouchMove?.(event)
          event.stopPropagation()
        }}
      />
    </TooltipPrimitive.Portal>
  )
})

export interface HintTooltipProps {
  /** 提示内容；为空时不显示，结构不变，触发元素在有无提示之间切换时不重建。 */
  content?: ReactNode
  /**
   * 触发元素会被禁用时传入：外层多一个触发区承接悬停与聚焦，用来说明为什么不能用；外层一直保留，
   * 元素在可用与禁用之间切换时不重建、焦点不丢。不传时提示直接挂在元素上，不改变布局。
   */
  disabled?: boolean
  side?: TooltipContentProps['side']
  /** 禁用说明外层的布局，如撑满所在格子。 */
  className?: string
  /** 禁用时外层触发区的读屏名字，一般就是按钮文字；不传时读屏只读到提示。 */
  label?: string
  tabIndex?: number
  children: ReactNode
}

/**
 * 给按钮、选择器等触发元素加共享悬停提示，禁用原因也走它；悬停提示不再用原生 title。
 * 只有提示与触发元素上的文字不同时才用。
 */
export function HintTooltip({
  content,
  disabled,
  side,
  className,
  label,
  tabIndex,
  children
}: HintTooltipProps): React.JSX.Element {
  const hasContent =
    content !== undefined && content !== null && content !== false && content !== ''
  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          {disabled === undefined ? (
            children
          ) : (
            <DisabledHintTrigger
              active={disabled && hasContent}
              label={label}
              tabIndex={tabIndex ?? 0}
              className={className}
            >
              {children}
            </DisabledHintTrigger>
          )}
        </TooltipTrigger>
        {hasContent ? <TooltipContent side={side}>{content}</TooltipContent> : null}
      </Tooltip>
    </TooltipProvider>
  )
}
